"use client";

import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { forwardRef, useCallback, useRef } from "react";
import { useShouldAutoFocus } from "@/hooks/use-should-auto-focus";
import type { WithAutoFocusOn } from "@/types";
import { Link } from "./link";

const styles = {
  base: [
    // Base
    "relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-control border text-base/6 font-semibold",
    // Sizing
    "px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6",
    // Focus
    "focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-ui-ring",
    // Disabled
    // HeadlessUI sets `data-disabled` when `disabled` prop is used.
    // When disabled via an ancestor <fieldset disabled>, the element matches `:disabled`
    // but does NOT necessarily get `data-disabled`, so we must style both.
    "data-disabled:opacity-50 disabled:opacity-50",
    // Icon
    "*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]",
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    "border-transparent bg-(--btn-border)",
    // Dark mode: border is rendered on `after` so background is set to button background
    "dark:bg-(--btn-bg)",
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-control)-1px)] before:bg-(--btn-bg)",
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    "before:shadow-control",
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    "dark:before:hidden",
    // Dark mode: Subtle white outline is applied using a border
    "dark:border-ui-border/5",
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-control)-1px)]",
    // Inner highlight shadow
    "after:shadow-[inset_0_1px_--theme(--color-ui-fg-on-accent/15%)]",
    // White overlay on hover
    "data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)",
    // Dark mode: `after` layer expands to cover entire button
    "dark:after:-inset-px dark:after:rounded-control",
    // Disabled
    "data-disabled:before:shadow-none data-disabled:after:shadow-none",
    "disabled:before:shadow-none disabled:after:shadow-none",
    // Avoid hover overlays while disabled via <fieldset disabled>.
    "disabled:data-active:after:bg-transparent disabled:data-hover:after:bg-transparent",
  ],
  outline: [
    // Base
    "border-ui-border/10 text-ui-fg data-active:bg-ui-row-overlay/2.5 data-hover:bg-ui-row-overlay/2.5",
    // Dark mode
    "dark:border-ui-border/15 dark:text-ui-fg dark:[--btn-bg:transparent] dark:data-active:bg-ui-row-overlay/5 dark:data-hover:bg-ui-row-overlay/5",
    // Icon
    "[--btn-icon:var(--ui-icon-muted)] data-active:[--btn-icon:var(--ui-icon-hover)] data-hover:[--btn-icon:var(--ui-icon-hover)] dark:data-active:[--btn-icon:var(--ui-icon-muted)] dark:data-hover:[--btn-icon:var(--ui-icon-muted)]",
    // Avoid hover backgrounds while disabled via <fieldset disabled>.
    "disabled:data-active:bg-transparent disabled:data-hover:bg-transparent",
  ],
  plain: [
    // Base
    "border-transparent text-ui-fg data-active:bg-ui-row-overlay/5 data-hover:bg-ui-row-overlay/5",
    // Dark mode
    "dark:text-ui-fg dark:data-active:bg-ui-row-overlay/10 dark:data-hover:bg-ui-row-overlay/10",
    // Icon
    "[--btn-icon:var(--ui-icon-muted)] data-active:[--btn-icon:var(--ui-icon-hover)] data-hover:[--btn-icon:var(--ui-icon-hover)] dark:[--btn-icon:var(--ui-icon-muted)] dark:data-active:[--btn-icon:var(--ui-icon-muted)] dark:data-hover:[--btn-icon:var(--ui-icon-muted)]",
    // Avoid hover backgrounds while disabled via <fieldset disabled>.
    "disabled:data-active:bg-transparent disabled:data-hover:bg-transparent",
  ],
  colors: {
    neutral: [
      "text-ui-button-solid-fg [--btn-bg:var(--color-ui-button-solid-bg)] [--btn-border:var(--color-ui-button-solid-border)]/90 [--btn-hover-overlay:var(--color-ui-button-solid-hover-overlay)]/10",
      "dark:text-ui-button-solid-fg dark:[--btn-bg:var(--color-ui-button-solid-bg)] dark:[--btn-hover-overlay:var(--color-ui-button-solid-hover-overlay)]/5",
      "[--btn-icon:var(--color-ui-button-solid-icon)] data-active:[--btn-icon:var(--color-ui-button-solid-icon-hover)] data-hover:[--btn-icon:var(--color-ui-button-solid-icon-hover)]",
    ],
    danger: [
      "text-ui-button-danger-fg [--btn-bg:var(--color-ui-button-danger-bg)] [--btn-border:var(--color-ui-button-danger-border)]/90 [--btn-hover-overlay:var(--color-ui-button-danger-hover-overlay)]/10",
      "dark:text-ui-button-danger-fg dark:[--btn-bg:var(--color-ui-button-danger-bg)] dark:[--btn-hover-overlay:var(--color-ui-button-danger-hover-overlay)]/5",
      "[--btn-icon:var(--color-ui-button-danger-icon)] data-active:[--btn-icon:var(--color-ui-button-danger-icon-hover)] data-hover:[--btn-icon:var(--color-ui-button-danger-icon-hover)]",
    ],
    success: [
      "text-ui-button-success-fg [--btn-bg:var(--color-ui-button-success-bg)] [--btn-border:var(--color-ui-button-success-border)]/90 [--btn-hover-overlay:var(--color-ui-button-success-hover-overlay)]/10",
      "dark:text-ui-button-success-fg dark:[--btn-bg:var(--color-ui-button-success-bg)] dark:[--btn-hover-overlay:var(--color-ui-button-success-hover-overlay)]/5",
      "[--btn-icon:var(--color-ui-button-success-icon)] data-active:[--btn-icon:var(--color-ui-button-success-icon-hover)] data-hover:[--btn-icon:var(--color-ui-button-success-icon-hover)]",
    ],
    primary: [
      "text-ui-button-primary-fg [--btn-bg:var(--color-ui-button-primary-bg)] [--btn-border:var(--color-ui-button-primary-border)]/90 [--btn-hover-overlay:var(--color-ui-button-primary-hover-overlay)]/10",
      "dark:text-ui-button-primary-fg dark:[--btn-bg:var(--color-ui-button-primary-bg)] dark:[--btn-hover-overlay:var(--color-ui-button-primary-hover-overlay)]/5",
      "[--btn-icon:var(--color-ui-button-primary-icon)] data-active:[--btn-icon:var(--color-ui-button-primary-icon-hover)] data-hover:[--btn-icon:var(--color-ui-button-primary-icon-hover)]",
    ],
    info: [
      "text-ui-button-info-fg [--btn-bg:var(--color-ui-button-info-bg)] [--btn-border:var(--color-ui-button-info-border)]/90 [--btn-hover-overlay:var(--color-ui-button-info-hover-overlay)]/10",
      "dark:text-ui-button-info-fg dark:[--btn-bg:var(--color-ui-button-info-bg)] dark:[--btn-hover-overlay:var(--color-ui-button-info-hover-overlay)]/5",
      "[--btn-icon:var(--color-ui-button-info-icon)] data-active:[--btn-icon:var(--color-ui-button-info-icon-hover)] data-hover:[--btn-icon:var(--color-ui-button-info-icon-hover)]",
    ],
  },
};

export type ButtonProps = (
  | { color?: keyof typeof styles.colors; outline?: never; plain?: never }
  | { color?: never; outline: true; plain?: never }
  | { color?: never; outline?: never; plain: true }
) & {
  className?: string;
  children: React.ReactNode;
  autoFocus?: boolean;
  ringOnFocus?: boolean;
} & WithAutoFocusOn &
  (
    | ({ href?: never } & Omit<Headless.ButtonProps, "as" | "className">)
    | ({ href: string } & Omit<
        React.ComponentPropsWithoutRef<typeof Link>,
        "className"
      >)
  );

export const Button = forwardRef(function Button(
  {
    color,
    outline,
    plain,
    className,
    children,
    autoFocus,
    autoFocusOn,
    ringOnFocus,
    ...props
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const resolvedAutoFocus = useShouldAutoFocus(autoFocus, autoFocusOn);
  const resolvedRingOnFocus = ringOnFocus ?? resolvedAutoFocus;
  const wasPointerDown = useRef(false);

  const handlePointerDown = useCallback(() => {
    wasPointerDown.current = true;
  }, []);

  const handlePointerUp = useCallback(() => {
    wasPointerDown.current = false;
  }, []);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLButtonElement>) => {
    if (!wasPointerDown.current) {
      e.currentTarget.setAttribute("data-focus", "");
    }
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLButtonElement>) => {
    e.currentTarget.removeAttribute("data-focus");
  }, []);

  const classes = clsx(
    className,
    styles.base,
    outline
      ? styles.outline
      : plain
        ? styles.plain
        : clsx(styles.solid, styles.colors[color ?? "neutral"]),
  );

  return typeof props.href === "string" ? (
    <Link
      {...props}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
    >
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button
      {...props}
      autoFocus={resolvedAutoFocus}
      data-autofocus={resolvedAutoFocus ? "true" : undefined}
      className={clsx(
        classes,
        "disabled:cursor-not-allowed enabled:cursor-pointer",
      )}
      tabIndex={props.disabled ? -1 : (props.tabIndex ?? 0)}
      ref={ref}
      {...(resolvedRingOnFocus && {
        onPointerDown: handlePointerDown,
        onPointerUp: handlePointerUp,
        onFocus: handleFocus,
        onBlur: handleBlur,
      })}
    >
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  );
});

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}
